import { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAppDataContext } from '../../context/AppDataContext';
import { useProducts } from '../../context/ProductsContext';
import { useSync } from '../../context/SyncContext';
import { useTranslation } from '../../i18n/index.jsx';
import Icon from '../common/Icon';
import LoadingSpinner from '../common/LoadingSpinner';
import AIFloatingButton from '../common/AIFloatingButton';
import { useCart } from '../../context/CartContext';
import { commitOrder } from '../../helpers/orderHelpers';

// Core page (always loaded with shell)
import AdminDashboard from './AdminDashboard';

// Lazy-loaded routes — split into separate chunks
const Dashboard = lazy(() => import('../../pages/Dashboard'));
const ProductsPage = lazy(() => import('./ProductsPage'));
const OrdersPage = lazy(() => import('./OrdersPage'));
const FinancePage = lazy(() => import('./FinancePage'));
const CustomersPage = lazy(() => import('./CustomersPage'));
const SettingsPage = lazy(() => import('./SettingsPage'));
const PurchaseOrderPage = lazy(() => import('./PurchaseOrderPage'));
const SalesRecordPage = lazy(() => import('./SalesRecordPage'));
const SupplierPage = lazy(() => import('./SupplierPage'));
const IntakePage = lazy(() => import('../intake/IntakePage'));
const CustomerDetailPage = lazy(() => import('./CustomerDetailPage'));

// Lazy-loaded modals (heavy — html2canvas, jsPDF)
const POSModal = lazy(() => import('../modals/POSModal'));
const PettyCashModal = lazy(() => import('../modals/PettyCashModal'));

// Route → bottom nav tab mapping
const ROUTE_TO_TAB = {
  '/': 'home',
  '/inventory': 'inventory',
  '/orders': 'orders',
  '/customers': 'customers',
  '/settings': 'settings',
  '/finance': 'settings',
  '/purchases': 'settings',
  '/sales-records': 'settings',
  '/suppliers': 'settings',
  '/intake': null,
};

function getTabFromPath(pathname) {
  // Exact match first
  if (ROUTE_TO_TAB[pathname] !== undefined) return ROUTE_TO_TAB[pathname];
  // Prefix match for nested routes
  for (const [route, tab] of Object.entries(ROUTE_TO_TAB)) {
    if (route !== '/' && pathname.startsWith(route)) return tab;
  }
  return 'home';
}

export default function AdminApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, handleLogout } = useAuth();
  const { t } = useTranslation();
  const {
    logs, setLogs,
    orders, setOrders,
    customers, setCustomers,
    operatingCosts,
    distributors, setDistributors,
    tasks, setTasks,
    memos, setMemos,
    users, setUsers,
    pettyCash, setPettyCash,
  } = useAppDataContext();
  const { bjProducts, setBjProducts, marucoProducts, setMarucoProducts } = useProducts();
  const { syncStatus } = useSync();
  const { cartCount } = useCart();

  // ── Desktop detection (PC Dashboard only on ≥ 768px) ──
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [showAdminPOS, setShowAdminPOS] = useState(false);
  const [adminPOSBrand, setAdminPOSBrand] = useState('MARUCO');
  const [showPettyCash, setShowPettyCash] = useState(false);

  // ── Intake prefill ──
  const [intakePrefillSku, setIntakePrefillSku] = useState(null);

  const allProducts = [...bjProducts, ...marucoProducts];

  const activeTab = getTabFromPath(location.pathname);

  const handleQuickAction = (action) => {
    switch(action) {
      case 'addStock': navigate('/intake'); break;
      case 'newSale': setShowAdminPOS(true); break;
      case 'inventory': navigate('/inventory'); break;
      case 'customers': navigate('/customers'); break;
      case 'reports': navigate('/finance'); break;
      case 'orders': navigate('/orders'); break;
      case 'purchases': navigate('/purchases'); break;
      case 'lowstock': navigate('/inventory'); break;
    }
  };

  const handleAddStock = (product) => {
    setIntakePrefillSku(product?.sku || null);
    navigate('/intake');
  };

  const handleOpenIntake = () => {
    navigate('/intake');
  };

  // ── Full-page Intake View (when on /intake route) ──
  const isIntakeRoute = location.pathname === '/intake';
  if (isIntakeRoute) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <IntakePage
          onClose={() => { setIntakePrefillSku(null); navigate(-1); }}
          prefillSku={intakePrefillSku}
        />
      </Suspense>
    );
  }

  // ── Full-screen PC Dashboard (desktop only, no header/bottom nav) ──
  if (location.pathname === '/' && isDesktop) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <Dashboard onNavigate={(tab) => {
          switch(tab) {
            case 'pos': setShowAdminPOS(true); break;
            case 'intake': navigate('/intake'); break;
            case 'inventory': case 'products': navigate('/inventory'); break;
            case 'orders': navigate('/orders'); break;
            case 'reports': case 'finance': navigate('/finance'); break;
            case 'customers': navigate('/customers'); break;
            case 'suppliers': navigate('/suppliers'); break;
            default: break;
          }
        }} />
        {showAdminPOS && (
          <POSModal
            bjProducts={bjProducts}
            marucoProducts={marucoProducts}
            user={user}

            onClose={() => setShowAdminPOS(false)}
            onComplete={async (saleData) => {
              return commitOrder({
                saleData,
                user,
                updateStock: (items) => {
                  items.forEach(item => {
                    if (item.brand === 'BJ') {
                      setBjProducts(prev => prev.map(p => {
                        if (p.id !== item.id) return p;
                        const updates = { stock: (p.stock || 0) - item.qty };
                        if (item.savePrice) updates.price = item.price;
                        return { ...p, ...updates };
                      }));
                    } else {
                      setMarucoProducts(prev => prev.map(p => {
                        if (p.id !== item.id) return p;
                        const updates = { stock: (p.stock || 0) - item.qty };
                        if (item.savePrice) updates.price = item.price;
                        return { ...p, ...updates };
                      }));
                    }
                  });
                },
              });
            }}
          />
        )}
      </Suspense>
    );
  }

  // ── POS completion handler (shared between desktop + mobile) ──
  const handlePOSComplete = async (saleData) => {
    return commitOrder({
      saleData,
      user,
      updateStock: (items) => {
        items.forEach(item => {
          if (item.brand === 'BJ') {
            setBjProducts(prev => prev.map(p => {
              if (p.id !== item.id) return p;
              const updates = { stock: (p.stock || 0) - item.qty };
              if (item.savePrice) updates.price = item.price;
              return { ...p, ...updates };
            }));
          } else {
            setMarucoProducts(prev => prev.map(p => {
              if (p.id !== item.id) return p;
              const updates = { stock: (p.stock || 0) - item.qty };
              if (item.savePrice) updates.price = item.price;
              return { ...p, ...updates };
            }));
          }
        });
      },
    });
  };

  return (
    <div className="min-h-screen bg-t-bg flex flex-col">
      {/* Header */}
      <header className="bg-t-card/80 backdrop-blur-sm border-b border-t-border px-4 py-2 safe-area-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${syncStatus === 'online' ? 'bg-green-500' : syncStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' : 'bg-red-500'}`} />
            <span className="text-t-secondary text-sm font-medium">EMH</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-t-secondary text-sm">{user.name}</span>
            <button onClick={handleLogout} className="text-t-tertiary hover:text-t-primary transition">
              <Icon name="LogOut" size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto p-4 pb-24">
        <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={
            <AdminDashboard onQuickAction={handleQuickAction} onOpenIntake={handleOpenIntake} />
          } />
          <Route path="/inventory" element={
            <ProductsPage onAddStock={handleAddStock} />
          } />
          <Route path="/orders" element={
            <OrdersPage onNewOrder={() => setShowAdminPOS(true)} />
          } />
          <Route path="/customers/:id" element={<CustomerDetailPage />} />
          <Route path="/customers" element={
            <CustomersPage />
          } />
          <Route path="/settings" element={
            <SettingsPage
              onShowFinance={() => navigate('/finance')}
              onShowPettyCash={() => setShowPettyCash(true)}
              onShowPurchases={() => navigate('/purchases')}
              onShowSalesRecords={() => navigate('/sales-records')}
              onShowSuppliers={() => navigate('/suppliers')}
              onShowUserManager={() => {}}
            />
          } />
          <Route path="/finance" element={
            <FinancePage onShowPayroll={() => {}} />
          } />
          <Route path="/purchases" element={
            <PurchaseOrderPage onClose={() => navigate('/settings')} />
          } />
          <Route path="/sales-records" element={
            <SalesRecordPage />
          } />
          <Route path="/suppliers" element={
            <SupplierPage onClose={() => navigate('/settings')} />
          } />
          {/* Catch-all: redirect unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-t-nav/80 backdrop-blur-xl border-t border-t-border safe-area-bottom z-40">
        <div className="flex">
          {[
            { id: 'home', path: '/', icon: 'Home', label: t('nav.home') },
            { id: 'inventory', path: '/inventory', icon: 'Package', label: t('nav.inventory') },
            { id: 'orders', path: '/orders', icon: 'ShoppingBag', label: t('nav.orders'), badge: orders.filter(o => o.status === 'pending').length, cartBadge: cartCount },
            { id: 'customers', path: '/customers', icon: 'Users', label: t('nav.customers') },
            { id: 'settings', path: '/settings', icon: 'Settings', label: t('nav.settings') },
          ].map(tab => (
            <button key={tab.id} onClick={() => navigate(tab.path)} className={`flex-1 py-2 pt-3 flex flex-col items-center relative transition ${activeTab === tab.id ? 'text-t-accent' : 'text-t-nav-inactive'}`}>
              {activeTab === tab.id && (
                <span className="absolute top-0.5 w-1 h-1 rounded-full bg-t-accent" />
              )}
              <Icon name={tab.icon} size={22} />
              <span className="text-[10px] mt-1 font-medium">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="absolute top-1 left-1/2 ml-2 w-4 h-4 bg-red-500 rounded-full text-white text-[9px] flex items-center justify-center font-bold">{tab.badge}</span>
              )}
              {tab.cartBadge > 0 && (
                <span className="absolute top-1 left-1/2 ml-6 min-w-[16px] h-4 bg-emerald-500 rounded-full text-white text-[9px] flex items-center justify-center font-bold px-0.5">{tab.cartBadge}</span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* Admin POS Sales Modal */}
      {showAdminPOS && (
        <Suspense fallback={<LoadingSpinner />}>
          <POSModal
            bjProducts={bjProducts}
            marucoProducts={marucoProducts}
            user={user}

            onClose={() => setShowAdminPOS(false)}
            onComplete={handlePOSComplete}
          />
        </Suspense>
      )}

      {/* Petty Cash Modal */}
      {showPettyCash && (
        <Suspense fallback={<LoadingSpinner />}>
          <PettyCashModal
            pettyCash={pettyCash}
            setPettyCash={setPettyCash}
            user={user}
            users={users}
            onClose={() => setShowPettyCash(false)}
          />
        </Suspense>
      )}

      {/* AI Floating Button */}
      <AIFloatingButton isAdmin={true} onOpenIntake={handleOpenIntake} />
    </div>
  );
}
